import React, { useEffect, useState } from 'react';
import { Container, Typography, Table, TableBody, TableCell, TableHead, TableRow, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { API_URL } from '../config';
import { useAuth } from '../AuthContext';

export default function AdminGroupsPage() {
  const { token } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState('');
  const [memberOpen, setMemberOpen] = useState(false);
  const [memberGroup, setMemberGroup] = useState(null);
  const [memberUserId, setMemberUserId] = useState('');
  const [memberAction, setMemberAction] = useState('add');
  const [memberRole, setMemberRole] = useState('user');
  const [memberError, setMemberError] = useState('');
  const [users, setUsers] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteGroup, setDeleteGroup] = useState(null);
  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_URL}/users`, { headers }).then(res => res.ok ? res.json() : []).then(data => setUsers(data));
  }, []);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [createError, setCreateError] = useState('');
  const fetchGroups = () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_URL}/groups`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => setGroups(data))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    fetchGroups();
  }, []);

  const handleCreateGroup = async () => {
    setCreateError('');
    try {
  const res = await fetch(`${API_URL}/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ name: newGroupName })
      });
      if (!res.ok) throw new Error('Failed to create group');
      setCreateOpen(false);
      setNewGroupName('');
      fetchGroups();
    } catch (err) {
      setCreateError('Failed to create group');
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>Manage Groups</Typography>
      <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={() => setCreateOpen(true)}>
        Create Group
      </Button>
      {loading ? <CircularProgress /> : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map(group => (
              <TableRow key={group.id}>
                <TableCell>{group.id}</TableCell>
                <TableCell>{group.name}</TableCell>
                <TableCell>
                  <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => {
                    setEditGroup(group);
                    setEditName(group.name);
                    setEditOpen(true);
                  }}>Edit</Button>
                  <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => {
                    setMemberGroup(group);
                    setMemberOpen(true);
                  }}>Members</Button>
                  <Button size="small" variant="outlined" color="error" onClick={() => {
                    setDeleteGroup(group);
                    setDeleteOpen(true);
                  }}>Delete</Button>
                </TableCell>
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>Edit Group</DialogTitle>
        <DialogContent>
          <TextField label="Group Name" fullWidth margin="normal" value={editName} onChange={e => setEditName(e.target.value)} />
          {editError && <Typography color="error">{editError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={async () => {
            try {
              await fetch(`${API_URL}/groups/${editGroup.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                body: JSON.stringify({ name: editName })
              });
              setEditOpen(false);
              fetchGroups();
            } catch {
              setEditError('Failed to update group');
            }
          }} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={memberOpen} onClose={() => setMemberOpen(false)}>
        <DialogTitle>Manage Members for {memberGroup?.name}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel id="member-action-label">Action</InputLabel>
            <Select labelId="member-action-label" value={memberAction} label="Action" onChange={e => setMemberAction(e.target.value)}>
              <MenuItem value="add">Add User</MenuItem>
              <MenuItem value="remove">Remove User</MenuItem>
              <MenuItem value="role">Change Role</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="member-user-label">User</InputLabel>
            <Select labelId="member-user-label" value={memberUserId} label="User" onChange={e => setMemberUserId(e.target.value)}>
              {users.map(u => <MenuItem key={u.id} value={u.id}>{u.nickname}</MenuItem>)}
            </Select>
          </FormControl>
          {(memberAction === 'role') && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="member-role-label">Role</InputLabel>
              <Select labelId="member-role-label" value={memberRole} label="Role" onChange={e => setMemberRole(e.target.value)}>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          )}
          {memberError && <Typography color="error">{memberError}</Typography>}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Current Members:</Typography>
          <ul>
            {memberGroup?.members?.map(m => <li key={m.user_id}>{m.nickname} ({m.role})</li>)}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberOpen(false)}>Cancel</Button>
          <Button onClick={async () => {
            try {
              const authHeaders = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
              if (memberAction === 'add') {
                await fetch(`${API_URL}/groups/${memberGroup.id}/join?user_id=${memberUserId}`, { method: 'POST', headers: authHeaders });
              } else if (memberAction === 'remove') {
                await fetch(`${API_URL}/groups/${memberGroup.id}/leave?user_id=${memberUserId}`, { method: 'POST', headers: authHeaders });
              } else if (memberAction === 'role') {
                await fetch(`${API_URL}/groups/${memberGroup.id}/assign-role?user_id=${memberUserId}&role=${memberRole}`, { method: 'POST', headers: authHeaders });
              }
              setMemberOpen(false);
              fetchGroups();
            } catch {
              setMemberError('Failed to update membership');
            }
          }} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete group {deleteGroup?.name}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={async () => {
            await fetch(`${API_URL}/groups/${deleteGroup.id}`, { method: 'DELETE', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
            setDeleteOpen(false);
            fetchGroups();
          }} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Create New Group</DialogTitle>
        <DialogContent>
          <TextField
            label="Group Name"
            fullWidth
            margin="normal"
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            required
          />
          {createError && <Typography color="error">{createError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateGroup} variant="contained" color="primary">Create</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
